import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import CompanySwitcher from "@/components/CompanySwitcher";
import {
  LayoutDashboard, Users, Truck, FileBarChart, ShieldCheck, CalendarClock,
  Leaf, LogOut, Receipt, Camera, Route, Map, CalendarDays, Recycle, UserCog, FlaskConical,
  Command, Settings as SettingsIcon,
} from "lucide-react";

// Client-facing nav (always visible to clients; visible to admins too as read view).
// For staff, each entry is gated on a capability — staff without the cap won't see it.
const CLIENT_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, clientOnly: true },
  { to: "/app/sustainability", label: "Sustainability", icon: Leaf, testid: "nav-sustainability", clientOnly: true },
  { to: "/app/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports", clientOnly: true },
  { to: "/app/collections", label: "My Collections", icon: Truck, testid: "nav-my-collections", clientOnly: true },
  { to: "/app/pickups", label: "Pickup Requests", icon: CalendarClock, testid: "nav-pickups", clientOnly: true },
  { to: "/app/licenses", label: "Licenses", icon: ShieldCheck, testid: "nav-licenses", clientOnly: true },
];

// Admin/staff sidebar — mental model: what does an admin need to serve clients and run the business.
// 6 sections × 2–5 items each, grouped by purpose (today · clients · field · insights · team · setup).
const OPS_GROUPS = [
  {
    label: "Today",
    items: [
      { to: "/app/ops", label: "Command Centre", icon: Command, testid: "nav-ops", adminOnly: true, end: true },
    ],
  },
  {
    label: "Clients",
    items: [
      { to: "/app/clients",   label: "Directory",       icon: Users,        testid: "nav-clients",   cap: "clients.manage" },
      { to: "/app/pickups",   label: "Pickups",         icon: CalendarClock, testid: "nav-pickups",   cap: "pickups.manage" },
      { to: "/app/invoices",  label: "Invoices",        icon: Receipt,      testid: "nav-invoices",  cap: "invoices.write" },
      { to: "/app/licenses",  label: "Licences",        icon: ShieldCheck,  testid: "nav-licenses",  cap: "licenses.manage" },
      { to: "/app/guides",    label: "Resident Guides", icon: Recycle,      testid: "nav-guides",    adminOnly: true },
    ],
  },
  {
    label: "Field",
    items: [
      { to: "/app/scheduler",   label: "Scheduler",      icon: CalendarDays, testid: "nav-scheduler",   cap: "trips.dispatch" },
      { to: "/app/trips",       label: "Trips & Routes", icon: Route,        testid: "nav-trips",       cap: "trips.dispatch" },
      { to: "/app/fleet",       label: "Fleet Map",      icon: Map,          testid: "nav-fleet",       cap: "trips.dispatch" },
      { to: "/app/collections", label: "Collections",    icon: Truck,        testid: "nav-collections", cap: "collections.write" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/app",                label: "Dashboard",      icon: LayoutDashboard, testid: "nav-dashboard-admin", adminOnly: true, end: true },
      { to: "/app/sustainability", label: "Sustainability", icon: Leaf,            testid: "nav-sustainability",  cap: "reports.read" },
      { to: "/app/reports",        label: "Reports",        icon: FileBarChart,    testid: "nav-reports",         cap: "reports.read" },
    ],
  },
  {
    label: "Team",
    items: [
      { to: "/app/drivers", label: "Drivers", icon: Truck,   testid: "nav-drivers", adminOnly: true },
      { to: "/app/staff",   label: "Staff",   icon: UserCog, testid: "nav-staff",   adminOnly: true },
    ],
  },
  {
    label: "Setup",
    items: [
      { to: "/app/waste-catalog", label: "Waste Catalog", icon: FlaskConical,  testid: "nav-waste-catalog", adminOnly: true },
      { to: "/app/settings",      label: "Settings",      icon: SettingsIcon,  testid: "nav-settings",      adminOnly: true },
    ],
  },
];

export default function AppShell() {
  const { user, logout, hasCap } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const isAdmin = user?.role === "admin";
  const isInternal = isAdmin || user?.role === "staff";

  // For clients: show CLIENT_NAV items (except cap-gated ones that don't apply). For internal users: hide clientOnly rows.
  const clientNavForUser = (item) => {
    if (item.clientOnly) return user?.role === "client";
    if (isAdmin) return true;
    if (user?.role === "client") return true;
    // staff
    if (!item.cap) return true;
    return hasCap(item.cap);
  };

  const opsNavForUser = (item) => {
    if (!isInternal) return false;
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.cap) return hasCap(item.cap);
    return false;
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bone)" }}>
      <aside
        className="w-[260px] shrink-0 border-r sticky top-0 h-screen flex flex-col"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
      >
        <div className="px-6 pt-7 pb-6">
          <div data-testid="brand-logo">
            <Logo size={44} />
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500 pl-[56px]">
              {isInternal ? "Internal Portal" : "Client Portal"}
            </div>
          </div>
        </div>

        <div className="px-3 flex-1 flex flex-col gap-1 overflow-y-auto">
          <div className="px-3 pb-2 hbt-label">{isInternal ? "Overview" : "Navigate"}</div>
          {CLIENT_NAV.filter(clientNavForUser).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `hbt-nav-link ${isActive ? "active" : ""}`}
            >
              <item.icon className="icon w-[18px] h-[18px]" strokeWidth={1.8} />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {isInternal && OPS_GROUPS.map((group) => {
            const visible = group.items.filter(opsNavForUser);
            if (visible.length === 0) return null;
            return (
              <div key={group.label}>
                <div className="px-3 pt-5 pb-2 hbt-label">{group.label}</div>
                {visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) => `hbt-nav-link ${isActive ? "active" : ""}`}
                  >
                    <item.icon className="icon w-[18px] h-[18px]" strokeWidth={1.8} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="mb-3 px-2">
            <CompanySwitcher />
          </div>
          <div className="flex items-center gap-3 px-2 pb-3">
            <div
              className="w-9 h-9 rounded-full grid place-items-center font-semibold text-sm"
              style={{ background: "rgba(26,54,45,0.1)", color: "var(--forest)" }}
            >
              {(user?.name || "U").slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                {isAdmin ? "HBT Admin" : user?.role === "staff" ? "HBT Staff" : "Client"}
              </div>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="hbt-nav-link w-full text-left">
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
